import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { aiAPI, subscriptionAPI } from "@/lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  ArrowLeft, Send, Loader2, Crown, Plus, MessageSquare,
  Trash2, Bot, User,
} from "lucide-react";

export default function AIAssistant() {
  const navigate = useNavigate();
  const [isPro, setIsPro] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => { loadInitial(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const loadInitial = async () => {
    try {
      const subRes = await subscriptionAPI.status();
      setIsPro(subRes.data.is_pro);
      if (subRes.data.is_pro) {
        const sessRes = await aiAPI.sessions();
        setSessions(sessRes.data);
      }
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const loadSession = async (sid) => {
    try {
      const { data } = await aiAPI.session(sid);
      setSessionId(sid);
      setMessages(data.messages || []);
      setShowSidebar(false);
    } catch {
      toast.error("Failed to load conversation");
    }
  };

  const startNewChat = () => {
    setSessionId(null);
    setMessages([]);
    setInput("");
    setShowSidebar(false);
  };

  const deleteSession = async (sid) => {
    try {
      await aiAPI.deleteSession(sid);
      setSessions(sessions.filter((s) => s.session_id !== sid));
      if (sessionId === sid) startNewChat();
      toast.success("Conversation deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || streaming) return;

    const userMsg = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setStreaming(true);

    try {
      const response = await aiAPI.chat(text, sessionId || undefined);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantContent = "";
      let newSessionId = sessionId;

      // Add empty assistant message to fill via streaming
      setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === "delta") {
              assistantContent += data.content;
              setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: "assistant", content: assistantContent };
                return updated;
              });
            } else if (data.type === "done") {
              newSessionId = data.session_id;
            } else if (data.type === "error") {
              toast.error(data.content || "AI error");
            }
          } catch (parseErr) {
            console.error("SSE parse error:", parseErr);
          }
        }
      }

      if (newSessionId && newSessionId !== sessionId) {
        setSessionId(newSessionId);
      }
      // Refresh sessions list
      const sessRes = await aiAPI.sessions();
      setSessions(sessRes.data);
    } catch (err) {
      toast.error("Failed to get AI response");
      // Remove the empty assistant message on error
      setMessages((prev) => prev.filter((m) => m.content !== ""));
    } finally {
      setStreaming(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isPro) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4">
        <Crown className="w-12 h-12 text-amber-500" />
        <h2 className="text-xl font-bold">Pro Feature</h2>
        <p className="text-muted-foreground text-center">AI Assistant requires a Pro subscription.</p>
        <Button onClick={() => navigate("/subscription")}>Upgrade to Pro</Button>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card flex-shrink-0">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-base font-bold flex items-center gap-2">
                <Bot className="w-5 h-5 text-primary" />
                AI Assistant
              </h1>
              <p className="text-xs text-muted-foreground">Maintenance & trail planning powered by Claude</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={startNewChat} data-testid="new-chat-btn">
              <Plus className="w-4 h-4 mr-1" /> New Chat
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSidebar(!showSidebar)}
              data-testid="history-btn"
            >
              <MessageSquare className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden max-w-5xl mx-auto w-full">
        {/* Sidebar — chat history */}
        {showSidebar && (
          <div className="w-64 border-r border-border bg-card overflow-y-auto flex-shrink-0 p-3 space-y-1">
            <p className="text-xs font-medium text-muted-foreground mb-2 px-2">Chat History</p>
            {sessions.length === 0 ? (
              <p className="text-xs text-muted-foreground px-2">No conversations yet</p>
            ) : (
              sessions.map((s) => (
                <div
                  key={s.session_id}
                  className={`flex items-center justify-between p-2 rounded cursor-pointer text-sm group ${
                    sessionId === s.session_id ? "bg-primary/10 text-primary" : "hover:bg-muted"
                  }`}
                >
                  <span className="truncate flex-1" onClick={() => loadSession(s.session_id)}>
                    {s.title}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteSession(s.session_id); }}
                    className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive p-1"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        {/* Chat area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-16 text-muted-foreground">
                <Bot className="w-16 h-16 mx-auto mb-4 opacity-20" />
                <p className="text-lg font-medium">What can I help with?</p>
                <p className="text-sm mt-1 max-w-md mx-auto">
                  Ask about maintenance schedules, trail recommendations, gear suggestions, or trip planning.
                </p>
                <div className="flex flex-wrap justify-center gap-2 mt-6">
                  {[
                    "What maintenance should I do before a trail run?",
                    "Recommend trails near Moab for a stock Jeep",
                    "What recovery gear do I need?",
                  ].map((q) => (
                    <button
                      key={q}
                      onClick={() => { setInput(q); textareaRef.current?.focus(); }}
                      className="text-xs px-3 py-2 rounded-full border border-border hover:bg-muted transition-colors"
                      data-testid="suggestion-btn"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-3 ${msg.role === "user" ? "justify-end" : ""}`}>
                {msg.role === "assistant" && (
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <Bot className="w-4 h-4 text-primary" />
                  </div>
                )}
                <Card className={`max-w-[80%] ${
                  msg.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-card"
                }`}>
                  <CardContent className="p-3 text-sm leading-relaxed">
                    {msg.content ? (
                      msg.role === "assistant" ? (
                        <div className="prose prose-sm prose-emerald max-w-none [&_h1]:text-base [&_h1]:font-bold [&_h1]:mt-3 [&_h1]:mb-1 [&_h2]:text-sm [&_h2]:font-bold [&_h2]:mt-3 [&_h2]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1 [&_p]:my-1 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_strong]:font-bold [&_hr]:my-2 [&_code]:bg-muted [&_code]:px-1 [&_code]:rounded [&_code]:text-xs">
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </div>
                      ) : (
                        <span className="whitespace-pre-wrap">{msg.content}</span>
                      )
                    ) : (streaming && i === messages.length - 1 ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : "")}
                  </CardContent>
                </Card>
                {msg.role === "user" && (
                  <div className="w-7 h-7 rounded-full bg-zinc-200 flex items-center justify-center flex-shrink-0 mt-1">
                    <User className="w-4 h-4 text-zinc-600" />
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-border bg-card p-4 flex-shrink-0">
            <div className="flex items-end gap-2 max-w-3xl mx-auto">
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about maintenance, trails, gear..."
                rows={1}
                className="resize-none min-h-[44px] max-h-32"
                disabled={streaming}
                data-testid="chat-input"
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || streaming}
                size="icon"
                className="flex-shrink-0 h-[44px] w-[44px]"
                data-testid="send-btn"
              >
                {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
